import { t } from 'i18next';

import type { Party } from '@/features/party/types';
import { getPartyName } from '@/lib/party';
import { formatAddress } from '@/lib/utils/address';
import { dateOrUndefined } from '@/lib/utils/date';
import type { Action } from '@/types/actions';
import type { Distribution } from '@/types/distribution';

export type MeetingOptions = {
  requiredAttendees?: string[];
  optionalAttendees?: string[];
  start?: Date | string;
  end?: Date | string;
  location?: string;
  subject?: string;
  body?: string;
  meetingType?:
    | 'ONLINE'
    | 'PHYSICAL_AT_OFFICE'
    | 'PHYSICAL_AT_CLIENT'
    | 'PHONE'
    | 'UNKNOWN';
  officeAddress?:
    | string
    | {
        street?: string;
        housenr?: string;
        boxnr?: string | null;
        zipcode?: string;
        city?: string;
        country_code?: string;
      };
};

const getStart = (options: MeetingOptions) =>
  dateOrUndefined(options.start) ?? new Date();

const getEnd = (options: MeetingOptions, start: Date) => {
  let end =
    dateOrUndefined(options.end) ?? new Date(start.getTime() + 60 * 60 * 1000);

  if (end.getTime() <= start.getTime())
    end = new Date(start.getTime() + 60 * 60 * 1000);

  return end;
};

const getLocation = (options: MeetingOptions) => {
  if (options.location) return options.location;

  switch (options.meetingType) {
    case 'PHYSICAL_AT_OFFICE':
      return formatAddress(options.officeAddress) ?? 'Kantoor';
    case 'PHYSICAL_AT_CLIENT':
      return 'Bij de klant';
    case 'PHONE':
      return 'Telefonisch';
    case 'ONLINE':
    default:
      return 'Online';
  }
};

const parseAttendees = (attendees: Maybe<string[]>) =>
  (attendees ?? []).map(
    (attendee) =>
      ({
        emailAddress: attendee,
        displayName: attendee,
      }) as Office.EmailAddressDetails,
  );

export const openMeetingRequest = (options: MeetingOptions) => {
  const start = getStart(options);
  const end = getEnd(options, start);
  const location = getLocation(options);
  const requiredAttendees = parseAttendees(options.requiredAttendees);
  const optionalAttendees = parseAttendees(options.optionalAttendees);
  const body = options.body ?? '';
  const subject = options.subject ?? 'Afspraak';

  const mailbox = Office?.context?.mailbox;

  if (typeof mailbox?.displayNewAppointmentForm !== 'function')
    throw new Error('displayNewAppointmentForm is not available');

  mailbox.displayNewAppointmentForm({
    start,
    end,
    location,
    body,
    subject,
    requiredAttendees,
    optionalAttendees,
    resources: [],
  });
};

const buildMeetingBody = (action: Action, party: Maybe<Party>) => {
  let bodyString = '';

  if (party) {
    bodyString += `${t('MEETING_FIELD_PARTY')}: <a href="${import.meta.env.VITE_PLATFORM_HOST}/parties/${party.id}">${getPartyName(party)}</a><br />`;
  }

  if (action.metadata && 'goal' in action.metadata) {
    bodyString += `${t('MEETING_FIELD_GOAL')}: ${action.metadata.goal}<br />`;
  }

  if (party?.telephonenr) {
    bodyString += `${t('MEETING_FIELD_PHONE')}: ${party.telephonenr}`;
  }

  if (action.metadata && 'description' in action.metadata) {
    bodyString += `${t('MEETING_FIELD_CONTEXT')}: ${action.metadata.context}<br />`;
  }

  return bodyString;
};

export const createMeetingFromAction = (
  action: Action,
  party: Maybe<Party>,
  address: Maybe<Distribution['address']>,
): MeetingOptions => {
  return {
    subject: action.metadata?.title as string | undefined,
    location: (address && formatAddress(address)) ?? undefined,
    requiredAttendees: action.metadata?.attendees as string[] | undefined,
    meetingType: action.metadata?.meeting_type as
      | MeetingOptions['meetingType']
      | undefined,
    start: dateOrUndefined(action.metadata?.start_time),
    end: dateOrUndefined(action.metadata?.end_time),
    body: buildMeetingBody(action, party),
  };
};
